import type { BossSeason } from "../core/talent-tracking/seasons.js"

import { getCsrfTokenFromDocumentCookie } from "./auth-api"
export type TalentTrackingSeason = BossSeason
export type TalentTrackingCategory = "offensive" | "defensive" | "strategic"

export type TalentProgressTalent = {
  id: string
  bossId: string
  category: TalentTrackingCategory
  tier: number
  description: string
  reward: string
  completed: boolean
  missingSince: string | null
  createdAt: string
  updatedAt: string
}

export type TalentProgressBoss = {
  id: string
  season: TalentTrackingSeason
  name: string
  sortOrder: number
  talentIds: string[]
  completedTalentIds: string[]
  missingTalentIds: string[]
  completedCount: number
  missingCount: number
  talents: TalentProgressTalent[]
  talentsByCategory: Record<TalentTrackingCategory, TalentProgressTalent[]>
  createdAt: string
  updatedAt: string
}

export type TalentProgressSeason = {
  season: TalentTrackingSeason
  bossIds: string[]
  bosses: TalentProgressBoss[]
  completedCount: number
  totalCount: number
}

export type UserTalentProgressSnapshot = {
  userId: string
  displayName?: string | null
  seasons: TalentProgressSeason[]
  bosses: TalentProgressBoss[]
  bossesById: Record<string, TalentProgressBoss>
  completedTalentIds: string[]
  missingTalentIds: string[]
  completionRate: number
}

export type MissingTalentMember = {
  userId: string
  displayName: string | null
}

export type MissingTalentSummary = {
  id: string
  bossId: string
  category: TalentTrackingCategory
  tier: number
  description: string
  reward: string
  missingUserIds: string[]
  missingMembers: MissingTalentMember[]
  missingCount: number
  createdAt: string
  updatedAt: string
}

export type MissingBossSummary = {
  id: string
  season: TalentTrackingSeason
  name: string
  sortOrder: number
  talentIds: string[]
  missingUserIds: string[]
  missingCount: number
  talents: MissingTalentSummary[]
  talentsByCategory: Record<TalentTrackingCategory, MissingTalentSummary[]>
  createdAt: string
  updatedAt: string
}

export type TalentTrackingOverview = {
  members: MissingTalentMember[]
  bosses: MissingBossSummary[]
  bossesById: Record<string, MissingBossSummary>
}

const readErrorMessage = async (
  response: Response,
  fallback: string,
): Promise<string> => {
  try {
    const body = (await response.json()) as {
      error?: { message?: string }
      message?: string
    }
    return body.error?.message ?? body.message ?? fallback
  } catch {
    return fallback
  }
}

export const talentTrackingApi = {
  getMyProgress: async (): Promise<UserTalentProgressSnapshot> => {
    const response = await fetch("/api/talent-tracking/me", {
      method: "GET",
      credentials: "include",
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to load talent progress"),
      )
    }

    const payload = (await response.json()) as {
      item: UserTalentProgressSnapshot
    }
    return payload.item
  },
  getOverview: async (params?: {
    season?: string
    bossId?: string
  }): Promise<TalentTrackingOverview> => {
    const query = new URLSearchParams()
    if (params?.season) query.set("season", params.season)
    if (params?.bossId) query.set("bossId", params.bossId)

    const response = await fetch(
      `/api/talent-tracking/overview${query.size > 0 ? `?${query.toString()}` : ""}`,
      {
        method: "GET",
        credentials: "include",
      },
    )

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to load talent overview"),
      )
    }

    const payload = (await response.json()) as {
      item: TalentTrackingOverview
    }
    return payload.item
  },
  getMemberProgress: async (
    userId: string,
  ): Promise<UserTalentProgressSnapshot> => {
    const response = await fetch(`/api/talent-tracking/members/${userId}`, {
      method: "GET",
      credentials: "include",
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(
          response,
          "Failed to load member talent progress",
        ),
      )
    }

    const payload = (await response.json()) as {
      item: UserTalentProgressSnapshot
    }
    return payload.item
  },
  setCompleted: async (
    talentId: string,
  ): Promise<UserTalentProgressSnapshot> => {
    const csrfToken = getCsrfTokenFromDocumentCookie()
    if (!csrfToken) {
      throw new Error("Missing CSRF token")
    }

    const response = await fetch(`/api/talent-tracking/me/${talentId}`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "x-csrf-token": csrfToken,
      },
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to mark talent completed"),
      )
    }

    const payload = (await response.json()) as {
      item: UserTalentProgressSnapshot
    }
    return payload.item
  },
  setIncomplete: async (
    talentId: string,
  ): Promise<UserTalentProgressSnapshot> => {
    const csrfToken = getCsrfTokenFromDocumentCookie()
    if (!csrfToken) {
      throw new Error("Missing CSRF token")
    }

    const response = await fetch(`/api/talent-tracking/me/${talentId}`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        "x-csrf-token": csrfToken,
      },
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to unmark talent completed"),
      )
    }

    const payload = (await response.json()) as {
      item: UserTalentProgressSnapshot
    }
    return payload.item
  },
  bulkSetStatus: async (
    talentIds: string[],
    completed: boolean,
  ): Promise<UserTalentProgressSnapshot> => {
    const csrfToken = getCsrfTokenFromDocumentCookie()
    if (!csrfToken) {
      throw new Error("Missing CSRF token")
    }

    const response = await fetch("/api/talent-tracking/me/batch", {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-csrf-token": csrfToken,
      },
      body: JSON.stringify({ talentIds, completed }),
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to bulk update talent status"),
      )
    }

    const payload = (await response.json()) as {
      item: UserTalentProgressSnapshot
    }
    return payload.item
  },
}
